import { Component, OnDestroy, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { CapabilityType } from 'src/app/models/service/capability';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { DateTimeFormatService } from 'src/app/services/util/date-time-format.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { AddBuildVersionFormComponent } from '../add-build-version-form/add-build-version-form.component';
import { SolutionVersionDetailsComponent } from '../solution-version-details/solution-version-details.component';
import { Permission } from '../../../models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import cloneDeep from 'lodash/cloneDeep';
import { ToastrErrorService } from 'src/app/services/util/toastr-error.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { RouterSegments } from 'src/app/models/router-segments';
import { GenericErrorComponent } from 'src/app/components/generic-error/generic-error.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';

const REQUIRED_PERMISSIONS = [
  Permission.ADD_SOLUTION_VERSION,
  Permission.EDIT_SOLUTION_VERSION,
  Permission.DELETE_SOLUTION_VERSION
];

@Component({
    selector: 'app-solution-versions',
    templateUrl: './solution-versions.component.html',
    styleUrls: ['./solution-versions.component.scss'],
    standalone: false
})
export class SolutionVersionsComponent  implements OnInit, OnDestroy {
  @ViewChild(MatSort, {static: true}) sort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
private destroyRef = inject(DestroyRef);

  CapabilityType = CapabilityType;
  RouterSegments = RouterSegments;
  solutionId: string;
  tenant: string;
  solutionVersions: any;
  dataSource: any;
  displayedColumns = ['version', 'releaseVersion', 'description', 'created', 'status', 'menu'];
  paginationSize = 10;
  pageIndex = 0;
  showPagination = true;
  sortInfo: any;
  showSpinner: any;
  breadcrumbItems: BreadcrumbItem[] = [];
  isReader: boolean;
  solutionName: string;
  Permission = Permission;
  permissionsMap: { [key: string]: boolean };
  isFailed = false;
  breadcrumbItem: any;
  urlSolution: any;
  solutionItem: any;

  constructor(private activatedRoute: ActivatedRoute, private toastrService: ToastrService, private customToastrService: CustomToastrService,
    private solutionsService: SolutionsService, private authService: AuthService, private sidenavService: SidenavService,
    public dateTimeFormatService: DateTimeFormatService, private permissionsService: PermissionsService,
    private toastrErrorService: ToastrErrorService, private dialog: MatDialog) {
      
     }

  ngOnInit(): void {
    this.permissionsMap = this.permissionsService.resolveUserPermissions(REQUIRED_PERMISSIONS);
    this.sidenavService.selectedMenu = localStorage.getItem('sideMenu');
    this.tenant = this.authService.principal.provider;
    this.solutionId = this.activatedRoute.snapshot.params.solutionId;
    this.solutionName = localStorage.getItem('solutionName');
    this.breadcrumbItem = localStorage.getItem('breadcrumbItem');
    this.urlSolution = localStorage.getItem('urlSolution');
    this.solutionItem = localStorage.getItem('solutionItem');
    this.breadcrumbItems.push({icon: 'home'},
      {label: this.breadcrumbItem },
      {label: this.solutionItem, url: this.urlSolution},
      {label: this.solutionName});
    this.getSolutionVersions();
  }

  getSolutionVersions(): void {
    this.showSpinner = true;
    this.isFailed = false;
    this.solutionVersions = [];
    this.loadData(this.solutionVersions);
    this.solutionsService.getSolutionVersions(this.solutionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.showSpinner = false;
          this.solutionVersions = data;
          this.loadData(this.solutionVersions);
        }
      },
      (error) => {
        this.showSpinner = false;
        this.isFailed = true;
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.customToastrService.errMessage(message, title);
        }, 500);
      });
  }

  loadData(data?): void {
    this.pageIndex = 0;
    if (this.sortInfo) {
      const sortedData = this.sortInstanceData(this.sortInfo);
      this.dataSource = new MatTableDataSource(sortedData?.data?.slice(this.pageIndex, this.paginationSize));
    } else {
      this.dataSource = new MatTableDataSource(data?.slice(this.pageIndex, this.paginationSize));
    }
  }

  sortData(event): void {
    this.sortInfo = event;
    this.showPagination = false;
    setTimeout(() => {
      this.showPagination = true;
    }, 10);
    this.pageIndex = 0;
    this.solutionVersions = this.sortInstanceData(event);
    this.dataSource = new MatTableDataSource(this.solutionVersions?.slice(this.pageIndex, this.paginationSize));
  }

  sortInstanceData(event): any {
    return this.solutionVersions?.sort((a, b) => {
      let comparatorResult = 0;
      switch (event.active) {
        case 'created':
          comparatorResult = a?.dateCreated && b?.dateCreated ? new Date(a?.dateCreated)?.getTime() - new Date(b?.dateCreated)?.getTime()
            : !a?.dateCreated ? -1 : 1;
          break;
        case 'version':
          comparatorResult = a?.version && b?.version ? a?.version?.localeCompare(b?.version) : !a?.version ? -1 : 1;
          break;
        case 'releaseVersion':
          comparatorResult = a?.releaseVersion && b?.releaseVersion ? a?.releaseVersion?.localeCompare(b?.releaseVersion) : !a?.releaseVersion ? -1 : 1;
          break;
        case 'description':
          comparatorResult = a?.description && b?.description ? a?.description?.localeCompare(b?.description)
            : !a?.description ? -1 : 1;
          break;
        case 'status':
          comparatorResult = a?.status && b?.status ? a?.status?.localeCompare(b?.status)
            : !a?.status ? -1 : 1;
          break;
        default:
          comparatorResult = a?.name && b?.name ? a?.name?.localeCompare(b?.name) : !a?.name ? -1 : 1;
          break;
      }
      return comparatorResult * (event.direction === 'asc' ? 1 : -1);
     });
  }

  pageChanged(event): any {
    if (event.length) {
      this.pageIndex = event.pageIndex;
      this.paginationSize = event.pageSize;
      const firstIndex = this.pageIndex * this.paginationSize;
      const data = this.solutionVersions.slice(firstIndex, firstIndex + this.paginationSize);
      this.dataSource = new MatTableDataSource(data);
    }
  }

  versionNameClick(event): void {
    this.solutionsService.getSolutionVersion(this.solutionId, event.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.sidenavService.openInRightSidenav(SolutionVersionDetailsComponent, {data});
        }
      },
      (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.customToastrService.errMessage(message, title);
        }, 500);
      });
  }

  /**
   * Edits the solution when user clicks on edit option from the list of options under menu button.
   * @params - the specific solution details of the solution clicked.
   */
  editSolutionVersion(data): void {
    if (!this.permissionsMap[Permission.EDIT_SOLUTION_VERSION]) {
      this.dialog.open(GenericErrorComponent, {
        minHeight: '342px',
        width: '566px',
        panelClass: 'flex-dialog'
      });  
    } else {
      localStorage.setItem('specForm', JSON.stringify(data?.installationSpecification));
      const editData = cloneDeep(data);
      this.sidenavService.openInRightSidenav(AddBuildVersionFormComponent, {editData, edit: true})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formData) => {
        if (!formData) {
          return;
        }
        formData.solutionId = this.solutionId;
        formData.id = data.id;
        this.dialog.open(ConfirmationDialogComponent, {
          panelClass: 'default-dialog-wrapper',
          width: '566px',
          autoFocus: false,
          data: {
            title: translate('SOLUTIONS.EDIT_VERSION_TITLE'),
            message:translate('SOLUTIONS.EDIT_VERSION_MESSAGE'),
            confirmButtonText: translate('BUTTONS.CONFIRM')
          }
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirm => {
          if (confirm) {
            this.solutionsService.putBuildVersion(formData)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
              if (response) {
                this.toastrService.success(translate('ADD_SOLUTION.BUILD_VERSION_EDITED'));
            setTimeout(() => {
              const title = translate('SOLUTIONS.SOLUTION');
              const message = translate('ADD_SOLUTION.BUILD_VERSION_EDITED');
              this.customToastrService.successMessage(message, title);
            }, 500);
              }
            },
            (error) => {
              const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
              this.dialog.open(ErrorDialogComponent, {
                minHeight: '342px',
                width: '630px',
                panelClass: 'flex-dialog',
                data: {
                  title: translate('ERROR_DIALOG.BUILD_VERSION_UPDATE_FAILED'),
                  description: errorMessage
                }
            });
            });
          }
        });
      });
    }
  }

  addNewBuildVersion(): void {
    this.sidenavService.openInRightSidenav(AddBuildVersionFormComponent, {edit: false})
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((formData) => {
      if (!formData) {
        return;
      }
      formData.solutionId = this.solutionId;
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: translate('SOLUTIONS.ADD_VERSION_TITLE'),
          message:translate('SOLUTIONS.ADD_VERSION_MESSAGE'),
          confirmButtonText: translate('BUTTONS.CONFIRM')
        }
      })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirm => {
          if (confirm) {
            this.solutionsService.postBuildVersion(formData)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(data => {
              if (data) {
                this.toastrService.success(translate('ADD_SOLUTION.BUILD_VERSION_ADDED'));
                localStorage.removeItem('versionForm');
                localStorage.removeItem('capabilities');
                localStorage.removeItem('specForm');
            setTimeout(() => {
              const title = translate('SOLUTIONS.SOLUTION');
              const message = translate('ADD_SOLUTION.BUILD_VERSION_ADDED');
              this.customToastrService.successMessage(message, title);
            }, 500);
              }
            },
            (error) => {
              const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
              this.dialog.open(ErrorDialogComponent, {
                minHeight: '342px',
                width: '630px',
                panelClass: 'flex-dialog',
                data: {
                  title: translate('ERROR_DIALOG.BUILD_VERSION_FAILED'),
                  description: errorMessage
                }
            });
            });
          }
        });
      });
  }

  deleteSolutionVersion(data): void {
    if (!this.permissionsMap[Permission.EDIT_SOLUTION_VERSION]) {
      this.dialog.open(GenericErrorComponent, {
        minHeight: '342px',
        width: '566px',
        panelClass: 'flex-dialog'
      });  
    } else {
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: translate('ADD_SOLUTION.DELETE_SOLUTION_VERSION'),
          message: translate('ADD_SOLUTION.DELETE_VERSION_CONFIRMATION'),
          confirmButtonText: translate('BUTTONS.DELETE')
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
      if (confirm) {
        this.solutionsService.deleteBuildVersion(data.solutionId, data.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(response => {
            if (response) {
              this.solutionVersions = this.solutionVersions.filter(version => version.id !== data.id);
              this.loadData(this.solutionVersions);
              this.toastrService.success(translate('SOLUTIONS.DELETE_BUILD_VERSION'));
            setTimeout(() => {
              const title = translate('SOLUTIONS.SOLUTION');
              const message = translate('SOLUTIONS.DELETE_BUILD_VERSION');
              this.customToastrService.successMessage(message, title);
            }, 500);
            }
          },
          (error) => {
            const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
            this.dialog.open(ErrorDialogComponent, {
              minHeight: '342px',
              width: '630px',
              panelClass: 'flex-dialog',
              data: {
                title: translate('ERROR_DIALOG.DELETE_SOLUTION_VERSION_FAILED'),
                description: errorMessage
              }
            });
          });
        }
      });
    }
  }

  ngOnDestroy(): void {
    localStorage.removeItem('versionForm');
    localStorage.removeItem('capabilities');
    localStorage.removeItem('specForm');
  }
}
